<template>
  <div class="user-manage-container">
    <div class="page-header">
      <h1 class="page-title">用户管理</h1>
      <p class="page-subtitle">管理系统用户信息，包括角色分配和VIP状态管理</p>
    </div>
    
    <el-card class="main-card" shadow="hover">
      <!-- 搜索区域 -->
      <div class="search-section">
        <el-form :inline="true" :model="searchForm" @submit.native.prevent="onSearch" class="search-form" :label-position="isMobile ? 'top' : 'right'">
          <el-form-item label="用户名">
            <el-input 
              v-model="searchForm.username" 
              placeholder="请输入用户名" 
              prefix-icon="el-icon-user"
              clearable
              style="width: 200px" 
              @keyup.enter.native="onSearch"
            />
          </el-form-item>
          <el-form-item label="角色">
            <el-select v-model="searchForm.role" placeholder="全部角色" clearable style="width: 150px">
              <el-option label="全部角色" value="" />
              <el-option label="管理员" value="admin" />
              <el-option label="普通用户" value="user" />
            </el-select>
          </el-form-item>
          <el-form-item label="状态">
            <el-select v-model="searchForm.status" placeholder="全部状态" clearable style="width: 150px">
              <el-option label="全部状态" value="" />
              <el-option label="正常" value="1" />
              <el-option label="已过期" value="0" />
            </el-select>
          </el-form-item>
          <el-form-item>
            <el-button type="primary" icon="el-icon-search" @click="onSearch" :loading="loading">
              搜索
            </el-button>
            <el-button icon="el-icon-refresh" @click="resetSearch">
              重置
            </el-button>
            <el-button type="success" icon="el-icon-plus" @click="showAddDialog">
              新增用户
            </el-button>
          </el-form-item>
        </el-form>
      </div>

      <!-- 数据表格 -->
      <div class="table-section">
        <el-table v-if="!isMobile" :data="users" border stripe style="width: 100%" v-loading="loading" :header-cell-style="{ background: '#f5f7fa', color: '#606266' }" class="user-table">
          <el-table-column prop="id" label="ID" width="80" align="center" />
          <el-table-column prop="username" label="用户名" width="140" />
          <el-table-column prop="email" label="邮箱" width="200">
            <template slot-scope="scope">
              <span v-if="scope.row.email" class="email-text">{{ scope.row.email }}</span>
              <span v-else class="empty-text">-</span>
            </template>
          </el-table-column>
          <el-table-column prop="role" label="角色" width="120" align="center">
            <template slot-scope="scope">
              <span :class="['tag-label', getRoleTagClass(scope.row.role)]">
                {{ scope.row.role === 'admin' ? '管理员' : '普通用户' }}
              </span>
            </template>
          </el-table-column>
          <el-table-column prop="vip_type" label="VIP类型" width="120" align="center">
            <template slot-scope="scope">
              <span v-if="scope.row.vip_type && scope.row.vip_type !== 'none'" :class="['tag-label', getVipTagClass(scope.row.vip_type)]">
                {{ getVipTypeText(scope.row.vip_type) }}
              </span>
              <span v-else class="empty-text">无VIP</span>
            </template>
          </el-table-column>
          <el-table-column prop="vip_expire_time" label="VIP到期时间" width="180" align="center">
            <template slot-scope="scope">
              <span v-if="scope.row.vip_expire_time" class="time-text">
                {{ formatDate(scope.row.vip_expire_time) }}
              </span>
              <span v-else class="empty-text">-</span>
            </template>
          </el-table-column>
          <el-table-column prop="status" label="状态" width="100" align="center">
            <template slot-scope="scope">
              <span :class="['tag-label', getStatusTagClass(scope.row.status)]">
                {{ scope.row.status ? '正常' : '已过期' }}
              </span>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="280" align="center" fixed="right">
            <template slot-scope="scope">
              <div class="action-buttons">
                <el-button 
                  type="warning" 
                  size="mini" 
                  icon="el-icon-edit"
                  @click="showEditDialog(scope.row)"
                >
                  编辑
                </el-button>
                <el-button 
                  type="danger" 
                  size="mini" 
                  icon="el-icon-delete"
                  @click="onDelete(scope.row)"
                >
                  删除
                </el-button>
                <el-button 
                  type="info" 
                  size="mini" 
                  icon="el-icon-key"
                  @click="resetPassword(scope.row)"
                >
                  重置密码
                </el-button>
              </div>
            </template>
          </el-table-column>
        </el-table>
        <!-- 移动端卡片列表 -->
        <div v-else class="mobile-card-list">
          <div v-for="item in users" :key="item.id" class="mobile-card">
            <div class="mobile-card-title">{{ item.username }}</div>
            <div class="mobile-card-row">邮箱：<span class="email-text">{{ item.email || '-' }}</span></div>
            <div class="mobile-card-row">角色：<span :class="['tag-label', getRoleTagClass(item.role)]">{{ item.role === 'admin' ? '管理员' : '普通用户' }}</span></div>
            <div class="mobile-card-row">VIP：<span v-if="item.vip_type && item.vip_type !== 'none'" :class="['tag-label', getVipTagClass(item.vip_type)]">{{ getVipTypeText(item.vip_type) }}</span><span v-else class="empty-text">无VIP</span></div>
            <div class="mobile-card-row">到期：<span class="time-text">{{ formatDate(item.vip_expire_time) || '-' }}</span></div>
            <div class="mobile-card-row">状态：<span :class="['tag-label', getStatusTagClass(item.status)]">{{ item.status ? '正常' : '已过期' }}</span></div>
            <div class="mobile-card-actions">
              <el-button type="warning" size="mini" icon="el-icon-edit" @click="showEditDialog(item)">编辑</el-button>
              <el-button type="danger" size="mini" icon="el-icon-delete" @click="onDelete(item)">删除</el-button>
              <el-button type="info" size="mini" icon="el-icon-key" @click="resetPassword(item)">重置密码</el-button>
            </div>
          </div>
          <div v-if="!users.length && !loading" class="no-data-tip">暂无数据</div>
        </div>
      </div>

      <!-- 分页 -->
      <div class="pagination-section">
        <el-pagination
          background
          layout="total, sizes, prev, pager, next, jumper"
          :current-page="searchForm.page"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="searchForm.pageSize"
          :total="total"
          @current-change="onPageChange"
          @size-change="onSizeChange"
        />
      </div>
    </el-card>
    <!-- 新增/编辑用户对话框 -->
    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="500px">
      <el-form :model="editForm" :rules="rules" ref="editForm" label-width="100px">
        <el-form-item label="用户名" prop="username">
          <el-input v-model="editForm.username" :disabled="isEdit" />
        </el-form-item>
        <el-form-item label="密码" prop="password" v-if="!isEdit">
          <el-input v-model="editForm.password" type="password" />
        </el-form-item>
        <el-form-item label="密码" v-if="isEdit">
          <el-input v-model="editForm.password" type="password" placeholder="留空则不修改密码" />
        </el-form-item>
        <el-form-item label="邮箱" prop="email">
          <el-input v-model="editForm.email" />
        </el-form-item>
        <el-form-item label="角色" prop="role">
          <el-select v-model="editForm.role" placeholder="请选择角色">
            <el-option label="普通用户" value="user" />
            <el-option label="管理员" value="admin" />
          </el-select>
        </el-form-item>
        <el-form-item label="VIP类型">
          <el-select v-model="editForm.vip_type" placeholder="请选择VIP类型" clearable>
            <el-option label="周卡" value="week" />
            <el-option label="月卡" value="month" />
            <el-option label="季卡" value="quarter" />
            <el-option label="年卡" value="year" />
            <el-option label="永久卡" value="forever" />
          </el-select>
        </el-form-item>
        <el-form-item label="VIP到期时间">
          <el-date-picker
            v-model="editForm.vip_expire_time"
            type="datetime"
            placeholder="选择VIP到期时间"
            format="yyyy-MM-dd HH:mm:ss"
            value-format="yyyy-MM-dd HH:mm:ss"
            :picker-options="{
              disabledDate(time) {
                return time.getTime() < Date.now() - 8.64e7;
              }
            }"
            :disabled="editForm.vip_type === 'forever'"
          />
          <span v-if="editForm.vip_type === 'forever'" style="color: #409EFF; margin-left: 10px;">永久有效</span>
        </el-form-item>

      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="confirmEdit" :loading="saving">确定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'UserManage',
  data() {
    return {
      searchForm: {
        username: '',
        role: '',
        status: '',
        page: 1,
        pageSize: 20
      },
      users: [],
      total: 0,
      loading: false,
      dialogVisible: false,
      saving: false,
      isEdit: false,
      editForm: {
        id: null,
        username: '',
        password: '',
        email: '',
        role: 'user',
        vip_type: '',
        vip_expire_time: ''
      },
      rules: {
        username: [{ required: true, message: '请输入用户名', trigger: 'blur' }],
        password: [{ required: true, message: '请输入密码', trigger: 'blur' }],
        role: [{ required: true, message: '请选择角色', trigger: 'change' }],
        email: [
          { type: 'email', message: '请输入正确的邮箱格式', trigger: 'blur' }
        ]
      },
      isMobile: false
    }
  },
  computed: {
    dialogTitle() {
      return this.isEdit ? '编辑用户' : '新增用户';
    }
  },
  created() {
    this.fetchUsers();
    this.checkMobile();
    window.addEventListener('resize', this.checkMobile);
  },
  destroyed() {
    window.removeEventListener('resize', this.checkMobile);
  },
  watch: {
    'editForm.vip_type'(newVal) {
      // 当选择永久卡时，清空到期时间
      if (newVal === 'forever') {
        this.editForm.vip_expire_time = '';
      }
    }
  },
  methods: {
    checkMobile() { this.isMobile = window.innerWidth < 768; },
    async fetchUsers() {
      this.loading = true;
      try {
        const params = {
          page: this.searchForm.page,
          pageSize: this.searchForm.pageSize,
          username: this.searchForm.username,
          role: this.searchForm.role,
          status: this.searchForm.status
        };
        const res = await this.$axios.get('/user/list', { params });
        if (res && res.code === 200) {
          this.users = res.data;
          this.total = res.total;
        } else {
          this.$message.error(res.message || '获取用户列表失败');
        }
      } catch (e) {
        this.$message.error(e.response?.data?.message || '网络错误');
      } finally {
        this.loading = false;
      }
    },
    onSearch() {
      this.searchForm.page = 1;
      this.fetchUsers();
    },
    resetSearch() {
      this.searchForm = {
        username: '',
        role: '',
        status: '',
        page: 1,
        pageSize: 20
      };
      this.fetchUsers();
    },
    onPageChange(page) {
      this.searchForm.page = page;
      this.fetchUsers();
    },
    onSizeChange(size) {
      this.searchForm.pageSize = size;
      this.searchForm.page = 1;
      this.fetchUsers();
    },
    showAddDialog() {
      this.isEdit = false;
      this.editForm = {
        id: null,
        username: '',
        password: '',
        email: '',
        role: 'user',
        vip_type: '',
        vip_expire_time: ''
      };
      this.dialogVisible = true;
    },
    showEditDialog(row) {
      console.log('Editing user row:', row);
      console.log('Original vip_expire_time:', row.vip_expire_time);
      
      this.isEdit = true;
      this.editForm = {
        id: row.id,
        username: row.username,
        password: '',
        email: row.email || '',
        role: row.role,
        vip_type: row.vip_type || '',
        vip_expire_time: row.vip_expire_time ? this.formatDateForPicker(row.vip_expire_time) : ''
      };
      
      console.log('Formatted vip_expire_time:', this.editForm.vip_expire_time);
      this.dialogVisible = true;
    },
    async confirmEdit() {
      this.$refs.editForm.validate(async valid => {
        if (!valid) return;
        this.saving = true;
        try {
          const data = { ...this.editForm };
          if (this.isEdit) {
            // 编辑时，如果密码为空则不传密码字段
            if (!data.password) {
              delete data.password;
            }
            const res = await this.$axios.post('/user/update', data);
            if (res && res.code === 200) {
              this.$message.success('修改成功');
              this.dialogVisible = false;
              this.fetchUsers();
            } else {
              this.$message.error(res.message || '修改失败');
            }
          } else {
            const res = await this.$axios.post('/user/add', data);
            if (res && res.code === 200) {
              this.$message.success('新增成功');
              this.dialogVisible = false;
              this.fetchUsers();
            } else {
              this.$message.error(res.message || '新增失败');
            }
          }
        } catch (e) {
          this.$message.error(e.response?.data?.message || '网络错误');
        } finally {
          this.saving = false;
        }
      });
    },
    async onDelete(row) {
      try {
        await this.$confirm('确定要删除这个用户吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        });
        const res = await this.$axios.post('/user/delete', { id: row.id });
        if (res && res.code === 200) {
          this.$message.success('删除成功');
          this.fetchUsers();
        } else {
          this.$message.error(res.message || '删除失败');
        }
      } catch (e) {
        if (e !== 'cancel') {
          this.$message.error(e.response?.data?.message || '网络错误');
        }
      }
    },
    async resetPassword(row) {
      try {
        await this.$confirm('确定要重置该用户的密码为 12345678 吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        });
        const res = await this.$axios.post('/user/update', { 
          id: row.id, 
          password: '12345678' 
        });
        if (res && res.code === 200) {
          this.$message.success('密码重置成功，新密码为：12345678');
          this.fetchUsers();
        } else {
          this.$message.error(res.message || '重置失败');
        }
      } catch (e) {
        if (e !== 'cancel') {
          this.$message.error(e.response?.data?.message || '网络错误');
        }
      }
    },
    getVipTypeText(type) {
      const types = { week: '周卡', month: '月卡', quarter: '季卡', year: '年卡', forever: '永久卡' };
      return types[type] || type;
    },
    getVipTypeColor(type) {
      const colors = { week: 'info', month: 'success', quarter: 'warning', year: 'danger' };
      return colors[type] || 'info';
    },
    formatDate(val) {
      if (!val) return '';
      const d = new Date(val);
      const pad = n => n.toString().padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    },
    formatDateForPicker(val) {
      if (!val) return '';
      try {
        console.log('formatDateForPicker input:', val, 'type:', typeof val);
        
        // 后端现在直接返回 yyyy-MM-dd HH:mm:ss 格式
        if (typeof val === 'string' && val.match(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/)) {
          console.log('formatDateForPicker output:', val);
          return val;
        }
        
        // 如果是其他格式，尝试转换
        const date = new Date(val);
        if (isNaN(date.getTime())) {
          console.warn('Invalid date value:', val);
          return '';
        }
        
        // 转换为 yyyy-MM-dd HH:mm:ss 格式
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        
        const formattedDate = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
        console.log('formatDateForPicker output:', formattedDate);
        return formattedDate;
      } catch (e) {
        console.error('Date formatting error:', e, 'Value:', val);
        return '';
      }
    },
    getRoleTagClass(role) {
      return role === 'admin' ? 'tag-role-admin' : 'tag-role-user';
    },
    getVipTagClass(type) {
      const map = {
        'week': 'tag-vip-week',
        'month': 'tag-vip-month',
        'quarter': 'tag-vip-season',
        'year': 'tag-vip-year',
        'forever': 'tag-vip-forever'
      };
      return map[type] || 'tag-vip-week';
    },
    getStatusTagClass(status) {
      return status ? 'tag-status-used' : 'tag-status-expired';
    }
  }
}
</script>

<style scoped>
.user-manage-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 24px;
  background: #f5f7fa;
  min-height: 100vh;
}

.page-header {
  margin-bottom: 24px;
  text-align: center;
}

.page-title {
  font-size: 28px;
  font-weight: 600;
  color: #303133;
  margin: 0 0 8px 0;
}

.page-subtitle {
  font-size: 14px;
  color: #909399;
  margin: 0;
}

.main-card {
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.search-section {
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #ebeef5;
}

.search-form {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.table-section {
  margin-bottom: 24px;
}

.user-table {
  border-radius: 6px;
  overflow: hidden;
}

.user-table .el-table__header-wrapper {
  background: #f5f7fa;
}

.email-text {
  color: #409eff;
  font-weight: 500;
}

.empty-text {
  color: #c0c4cc;
  font-style: italic;
}

.time-text {
  color: #606266;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 12px;
}

.pagination-section {
  display: flex;
  justify-content: center;
  padding-top: 16px;
  border-top: 1px solid #ebeef5;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .user-manage-container {
    padding: 4px;
  }
  .page-title {
    font-size: 1.1rem;
  }
  .search-form {
    flex-direction: column !important;
    align-items: stretch !important;
    display: flex !important;
  }
  .search-form .el-form-item {
    margin-bottom: 8px;
    width: 100%;
    display: flex;
    flex-direction: column;
  }
  .el-input,
  .el-select,
  .el-button {
    width: 100% !important;
    min-width: 0 !important;
    box-sizing: border-box;
  }
  .table-section {
    padding: 0;
  }
  .mobile-card-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin: 0 0 12px 0;
  }
  .mobile-card {
    background: #fff;
    border: 1px solid #e4e7ed;
    border-radius: 8px;
    padding: 14px 12px 10px 12px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .mobile-card-title {
    font-weight: 600;
    font-size: 15px;
    color: #333;
    margin-bottom: 2px;
    word-break: break-all;
  }
  .mobile-card-row {
    font-size: 13px;
    margin-bottom: 2px;
    word-break: break-all;
  }
  .mobile-card-actions {
    display: flex;
    gap: 8px;
    margin-top: 4px;
  }
}
@media (min-width: 768px) {
  .mobile-card-list { display: none; }
}

/* 操作按钮样式 */
.action-buttons {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
}

.action-buttons .el-button {
  width: 100%;
  margin: 0;
  justify-content: center;
}

/* 动画效果 */
.el-button {
  transition: all 0.3s ease;
}

.el-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.el-tag {
  transition: all 0.3s ease;
}

.el-tag:hover {
  transform: scale(1.05);
}

.tag-vip-week { background-color: #909399; color: white; }
.tag-vip-month { background-color: #67C23A; color: white; }
.tag-vip-season { background-color: #E6A23C; color: white; }
.tag-vip-year { background-color: #F56C6C; color: white; }
.tag-vip-forever { background-color: #409EFF; color: white; }

.tag-status-used { background-color: #67C23A; color: white; }
.tag-status-expired { background-color: #F56C6C; color: white; }

.tag-role-admin { background-color: #409EFF; color: white; }
.tag-role-user { background-color: #909399; color: white; }

.tag-label {
  display: inline-block;
  padding: 2px 8px;
  font-size: 12px;
  border-radius: 4px;
  white-space: nowrap;
}
</style>